<template>
  <div id="gatewayConfigurationBackhaul">
    <card :title="$t('gc-backhaul:backhaul')">
      <div slot="card-body">
        <div class="rounded border border-primary text-center text-uppercase p-3 mb-3">{{ $t('gc-backhaul:sortToDefinePriority') }}</div>

        <draggable v-model="wan.wanInterfaceTypes" animation="200" handle=".draggable" @end="onDragEnd">
          <div v-for="wanInterfaceType in wan.wanInterfaceTypes" :key="wanInterfaceType.wanInterfaceType" class="card mb-3">
            <div class="card-header draggable d-flex justify-content-between align-items-center">
              <h3 class="card-title mb-0">
                <template v-if="wanInterfaceType.wanInterfaceType === 'GSM'">{{ $t('gc-backhaul:gsm.title') | capitalize }}</template>
                <template v-if="wanInterfaceType.wanInterfaceType === 'ETHERNET'">{{ $t('gc-backhaul:ethernet.title') | capitalize }}</template>
                <template v-if="wanInterfaceType.wanInterfaceType === 'WLAN'">{{ $t('gc-backhaul:wlan.title') | capitalize }}</template>
              </h3>

              <span>{{ $t('gc-backhaul:priority') | capitalize }} {{ wanInterfaceType.index }}<i class="icon-move ml-2" /></span>
            </div>

            <template v-if="wanInterfaceType.wanInterfaceType === 'GSM' && gateway.interfaceVersion === 3">
              <div class="card-body">
                <div class="form-row">
                  <div class="form-group col-lg-6">
                    <label for="login" class="control-label">{{ $t('gc-backhaul:gsm.login') | capitalize }}</label>
                    <input id="login" v-model="gsmOperator.operatorLogin" :disabled="disabled" type="text" class="form-control">
                  </div>

                  <div class="form-group col-lg-6">
                    <label for="password" class="control-label">{{ $t('gc-backhaul:gsm.password') | capitalize }}</label>
                    <input id="password" v-model="gsmOperator.operatorPassword" :disabled="disabled" type="text" class="form-control">
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group col-lg-6">
                    <label for="apn" class="control-label">{{ $t('gc-backhaul:gsm.apn') | capitalize }}</label>
                    <input id="apn" v-model="gsmOperator.operatorApn" :disabled="disabled" type="text" class="form-control">
                  </div>

                  <div class="form-group col-lg-6">
                    <label for="pinCode" class="control-label">{{ $t('gc-backhaul:gsm.pinCode') | capitalize }}</label>
                    <input id="pinCode"
                           v-model="gsmOperator.operatorPinCode"
                           v-validate="'regex:^[0-9][0-9][0-9][0-9][0-9]?[0-9]?[0-9]?[0-9]?$'"
                           :disabled="disabled"
                           :data-vv-as="$t('gc-backhaul:gsm.pinCode')"
                           :class="{ 'is-invalid': errors.has('pinCode') }"
                           type="text"
                           name="pinCode"
                           class="form-control"
                    >
                    <div class="invalid-feedback">{{ errors.first('pinCode') }}</div>
                  </div>
                </div>
              </div>
            </template>

            <backhaul-gsm-operators v-if="wanInterfaceType.wanInterfaceType === 'GSM' && gateway.interfaceVersion === 4"
                                    v-model="wan.gsmOperators"
            />

            <template v-if="wanInterfaceType.wanInterfaceType === 'ETHERNET'">
              <div class="card-body">
                <div class="form-row">
                  <div class="form-group col-lg-6">
                    <label for="dhcp" class="control-label d-block">{{ $t('gc-backhaul:ethernet.dhcp') | capitalize }}</label>
                    <div class="pt-2">
                      <input id="dhcp" v-model="dhcp" :disabled="disabled" type="checkbox" name="dhcp">
                      <label for="dhcp" @click.stop.prevent="confirmDeactivateDhcp">{{ $t('gc-backhaul:ethernet.activateDhcp') | capitalize }}</label>
                    </div>
                  </div>

                  <div v-if="!dhcp" class="form-group col-lg-6">
                    <label for="defaultIp" class="control-label">{{ $t('gc-backhaul:ethernet.defaultIp') | capitalize }}</label>
                    <input id="defaultIp"
                           v-model="wan.defaultIp"
                           v-validate="'ip'"
                           :disabled="disabled"
                           :data-vv-as="$t('gc-backhaul:ethernet.defaultIp')"
                           :class="{ 'is-invalid': errors.has('defaultIp') }"
                           type="text"
                           name="defaultIp"
                           class="form-control"
                    >
                    <div class="invalid-feedback">{{ errors.first('defaultIp') }}</div>
                  </div>
                </div>

                <div v-if="!dhcp" class="form-row">
                  <div class="form-group col-lg-6">
                    <label for="defaultNetmask" class="control-label">{{ $t('gc-backhaul:ethernet.defaultNetmask') | capitalize }}</label>
                    <input id="defaultNetmask"
                           v-model="wan.defaultNetmask"
                           v-validate="'ip'"
                           :disabled="disabled"
                           :data-vv-as="$t('gc-backhaul:ethernet.defaultNetmask')"
                           :class="{ 'is-invalid': errors.has('defaultNetmask') }"
                           type="text"
                           name="defaultNetmask"
                           class="form-control"
                    >
                    <div class="invalid-feedback">{{ errors.first('defaultNetmask') }}</div>
                  </div>

                  <div class="form-group col-lg-6">
                    <label for="defaultGateway" class="control-label">{{ $t('gc-backhaul:ethernet.defaultGateway') | capitalize }}</label>
                    <input id="defaultGateway"
                           v-model="wan.defaultGateway"
                           v-validate="'ip'"
                           :disabled="disabled"
                           :data-vv-as="$t('gc-backhaul:ethernet.defaultGateway')"
                           :class="{ 'is-invalid': errors.has('defaultGateway') }"
                           type="text"
                           name="defaultGateway"
                           class="form-control"
                    >
                    <div class="invalid-feedback">{{ errors.first('defaultGateway') }}</div>
                  </div>
                </div>

                <div v-if="!dhcp && gateway.interfaceVersion === 4" class="form-row">
                  <div class="form-group col-lg-6">
                    <label for="dns1" class="control-label">{{ $t('gc-backhaul:ethernet.dns1') | capitalize }}</label>
                    <input id="dns1"
                           v-model="wan.dns1"
                           v-validate="'ipHostname'"
                           :disabled="disabled"
                           :data-vv-as="$t('gc-backhaul:ethernet.dns1')"
                           :class="{ 'is-invalid': errors.has('dns1') }"
                           type="text"
                           name="dns1"
                           class="form-control"
                    >
                    <div class="invalid-feedback">{{ errors.first('dns1') }}</div>
                  </div>

                  <div class="form-group col-lg-6">
                    <label for="dns2" class="control-label">{{ $t('gc-backhaul:ethernet.dns2') | capitalize }}</label>
                    <input id="dns2"
                           v-model="wan.dns2"
                           v-validate="'ipHostname'"
                           :disabled="disabled"
                           :data-vv-as="$t('gc-backhaul:ethernet.dns2')"
                           :class="{ 'is-invalid': errors.has('dns2') }"
                           type="text"
                           name="dns2"
                           class="form-control"
                    >
                    <div class="invalid-feedback">{{ errors.first('dns2') }}</div>
                  </div>
                </div>
              </div>
            </template>

            <template v-if="wanInterfaceType.wanInterfaceType === 'WLAN'">
              <div class="card-body">
                <div class="form-row">
                  <div class="form-group col-lg-6">
                    <label for="ssid" class="control-label">{{ $t('gc-backhaul:wlan.ssid') | capitalize }}</label>
                    <input id="ssid" v-model="wan.ssid" :disabled="disabled" type="text" class="form-control">
                  </div>

                  <div class="form-group col-lg-6">
                    <label for="mode" class="control-label d-block">{{ $t('gc-backhaul:wlan.mode') | capitalize }}</label>

                    <div class="pt-2">
                      <input id="WEP" v-model="wan.mode" :disabled="disabled" type="radio" value="WEP" name="mode">
                      <label for="WEP" class="control-label">WEP</label>

                      <input id="WPA" v-model="wan.mode" :disabled="disabled" type="radio" value="WPA" name="mode">
                      <label for="WPA" class="control-label">WPA</label>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label for="passwordWlan" class="control-label">{{ $t('gc-backhaul:wlan.wlanPassword') | capitalize }}</label>
                  <input id="passwordWlan" v-model="wan.passwordWlan" :disabled="disabled" type="text" class="form-control">
                </div>
              </div>
            </template>
          </div>
        </draggable>
      </div>

      <div slot="card-footer">
        <button id="sendButton" :disabled="disabled" class="btn btn-primary" @click="send">{{ $t('gc-backhaul:send') }}</button>
      </div>
    </card>

    <confirm id="deactivateDhcp"
             v-model="showDeactivateDhcpConfirm"
             :message="$t('gc-backhaul:ethernet.confirmDeactivateDhcpTitle')"
             with-body
             yes-no-buttons
             @confirm:cancel="setDhcpValue(true)"
             @confirm:validate="setDhcpValue(false)"
    >
      <template slot="confirm-body">{{ $t('gc-backhaul:ethernet.confirmDeactivateDhcp') | capitalize }}</template>
    </confirm>

    <delays :eui="eui" />
  </div>
</template>

<script>
import { isEmpty, sortBy, omitBy, pick } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

import Delays from './Delays'
import BackhaulGsmOperators from './BackhaulGsmOperators'
const Draggable = () => import(/* webpackChunkName: "l-draggable" */ 'vuedraggable')

export default {
  titleKey: 'gatewayConfBackhaul',

  components: { Draggable, BackhaulGsmOperators, Delays },

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      wan: {},
      gsmOperator: {},
      // boolean for checkbox, will be 'TRUE'/'FALSE' in clock object
      dhcp: false,
      getting: false,
      sending: false,
      showDeactivateDhcpConfirm: false
    }
  },

  computed: {
    ...mapGetters(['gateway']),

    disabled () {
      return isEmpty(this.wan) || this.sending
    }
  },

  watch: {
    eui () {
      // Reset data, and get data from new gateway
      this.wan = {}
    },

    gateway () {
      // Do not refresh is we olready have the data
      if (!isEmpty(this.wan) || this.getting) return

      this.getWan()
    }
  },

  created () {
    this.getWan()
  },

  methods: {
    async getWan () {
      if (isEmpty(this.gateway)) return

      if (!this.gateway.connection) {
        eventBus.$emit('message-info', this.$t('gc-backhaul:gatewayNotConnected'))
        return
      }

      this.getting = true
      let error, response
      [error, response] = await to(this.$api.getGatewayCurrentConfiguration(this.gateway, { groups: 'wan' }))
      if (!error && response) {
        let wan = response.data
        // Convert "TRUE"/"FALSE" to boolean for ptpMaster value
        this.dhcp = wan.dhcp === 'TRUE'
        // sort wan interface type objects by "index" attribute
        wan.wanInterfaceTypes = sortBy(wan.wanInterfaceTypes, 'index')
        this.wan = wan

        // When interface version equals 3, we use the first item of gsmOperators
        if (this.gateway.interfaceVersion === 3 && this.wan.gsmOperators.length >= 1) {
          this.gsmOperator = this.wan.gsmOperators[0]
        } else {
          this.gsmOperator = {
            operatorApn: '',
            operatorLogin: '',
            operatorPassword: '',
            operatorPinCode: ''
          }
        }
      }

      this.getting = false
    },

    confirmDeactivateDhcp () {
      // Display confirm dialog only when deactivating DHCP
      if (this.dhcp) this.showDeactivateDhcpConfirm = true
      // Else change value to true
      else this.dhcp = true
    },

    setDhcpValue (dhcpValue) {
      this.dhcp = dhcpValue
    },

    onDragEnd (a) {
      // set the "index" attributes as the index of the list
      for (let i = 0; i < this.wan.wanInterfaceTypes.length; i++) {
        this.wan.wanInterfaceTypes[i].index = i + 1
      }
    },

    async send () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      this.sending = true

      // Convert boolean to "TRUE"/"FALSE" for dhcp value
      this.wan.dhcp = this.dhcp === true ? 'TRUE' : 'FALSE'

      if (this.gateway.interfaceVersion === 3) this.wan.gsmOperators = [this.gsmOperator]

      const keys = [
        'wanInterfaceTypes',
        'dhcp', 'defaultIp', 'defaultNetmask', 'defaultGateway',
        'ssid', 'mode', 'passwordWlan',
        'gsmOperators'
      ]
      if (this.gateway.interfaceVersion === 4) keys.push('dns1', 'dns2')

      let error
      [error] = await to(this.$api.updateGatewayCurrentConfiguration(this.gateway, omitBy(pick(this.wan, keys), v => { return v === 'UNKNOWN' })))
      if (!error) eventBus.$emit('message-success', this.$t('gc-backhaul:sendSuccess'))

      this.sending = false
    }
  }
}
</script>

<style scoped lang="scss">
  .card-header.draggable {
    cursor: move;

    h3 + span {
      font-size: 1rem;

      i.icon-move {
        font-size: 1.2rem;
        vertical-align: text-bottom;
        cursor: move;
      }
    }
  }
</style>
